import React, { CSSProperties } from "react";
import styled from "@emotion/styled";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrIcon } from "@maven-surface/components/base/CrIcon";
import { nl2br } from "@maven-surface/components/helper/nl2br";
import { IconPackageName } from "@maven-surface/components/interface/Icon";
import "./ecoa.paragraph.scss";

type EcoaParagraphTextProps = { children: React.ReactNode } | { text: string | React.ReactNode };

type EcoaParagraphProps = EcoaParagraphTextProps & {
  align?: "CENTER" | "LEFT" | "RIGHT";
  margin?: 1 | 2 | 3 | 4 | 5;
  spacing?: 1 | 2 | 3 | 4 | 5;
  size?: "LARGE" | "X-LARGE" | "2X-LARGE" | "3X-LARGE" | "4X-LARGE" | "5X-LARGE" | "6X-LARGE" | "7X-LARGE" | "8X-LARGE";
  color?: "GRAY" | "WARNING" | "DANGER" | "SUCCESS" | "PRIMARY";
  bold?: boolean;
  icon?: IconPackageName;
  bullet?: boolean;
  style?: CSSProperties;
};

export function EcoaParagraph(props: EcoaParagraphProps) {
  const { align, margin, spacing, color, size, bold, icon, bullet, style } = props;

  const text = ((props) => {
    if ("children" in props) {
      return <>{props.children}</>;
    } else if (typeof props.text === "string") {
      return <>{nl2br(props.text)}</>;
    }

    return <>{props.text}</>;
  })(props);

  return (
    <StyledEcoaParagraph
      align={align}
      style={style}
      margin={margin}
      spacing={spacing}
      bold={bold}
      bullet={bullet}
      color={color}
      size={size}
      className={classNames("ecoa-paragraph", `--color-${String(color || "default").toLowerCase()}`, bullet && "--prefix-bullet")}
    >
      {!!icon && (
        <StyledEcoaParagraphIcon className={`--${icon.package.toLowerCase()}`}>
          <CrIcon icon={icon} />
        </StyledEcoaParagraphIcon>
      )}
      {text}
    </StyledEcoaParagraph>
  );
}

type ParagraphFontSizeProps = {
  fontSize: number;
  marginLeft: number;
  top: number;
  left: number;
  width: number;
  height: number;
};

const paragraphFontSize = ({ fontSize, marginLeft, top, left, width, height }: ParagraphFontSizeProps) => `
  font-size: ${fontSize}rem;
  .--prefix-bullet:before {
    margin-left: ${marginLeft}rem;
    top: ${top}rem;
    left: ${left}rem;
    width: ${width}rem;
    height: ${height}rem;
  }
`;

interface StyledEcoaParagraphProps
  extends Pick<EcoaParagraphProps, "align" | "margin" | "spacing" | "bold" | "bullet" | "color" | "size"> {}

const StyledEcoaParagraph = styled.p<StyledEcoaParagraphProps>`
  display: block;
  position: relative;
  margin: 10px 0;
  line-height: 140%;
  word-break: keep-all;

  ${({ bold }) => bold && "font-weight: bold;"};
  ${({ align }) => align && `text-align: ${align.toLocaleLowerCase()};`};
  ${({ margin }) => margin && `margin-left: ${margin ?? 0}rem;`};
  ${({ margin }) => margin && `margin-right: ${margin ?? 0}rem;`};
  ${({ spacing }) => spacing && `margin-top: ${spacing ?? 0}rem;`};
  ${({ spacing }) => spacing && `margin-bottom: ${spacing ?? 0}rem;`};

  ${({ bullet }) =>
    bullet &&
    `
      padding-left: 1.1rem;  

      &:before {
        content: "";
        display: inline-block;
        position: absolute;
        border-radius: 50%;
      }
    `}

  ${({ size }) =>
    size &&
    ((size) => {
      switch (size) {
        case "LARGE":
          return paragraphFontSize({ fontSize: 0.91, marginLeft: 0.4, top: 0.5, left: 0.1, width: 0.2, height: 0.2 });
        case "X-LARGE":
          return paragraphFontSize({ fontSize: 1, marginLeft: 0.3, top: 0.5, left: 0.1, width: 0.3, height: 0.3 });
        case "2X-LARGE":
          return paragraphFontSize({ fontSize: 1.125, marginLeft: 0.3, top: 0.55, left: 0.08, width: 0.3, height: 0.3 });
        case "3X-LARGE":
          return paragraphFontSize({ fontSize: 1.25, marginLeft: 0.3, top: 0.7, left: 0.05, width: 0.4, height: 0.4 });
        case "4X-LARGE":
          return paragraphFontSize({ fontSize: 1.5, marginLeft: 0.3, top: 0.8, left: 0.05, width: 0.4, height: 0.4 });
        case "5X-LARGE":
          return paragraphFontSize({ fontSize: 1.875, marginLeft: 0.3, top: 1.1, left: 0.05, width: 0.4, height: 0.4 });
        case "6X-LARGE":
          return paragraphFontSize({ fontSize: 2.25, marginLeft: 0.3, top: 1.3, left: 0.05, width: 0.5, height: 0.5 });
        case "7X-LARGE":
          return paragraphFontSize({ fontSize: 2.7, marginLeft: 0.3, top: 1.5, left: 0.05, width: 0.5, height: 0.5 });
        case "8X-LARGE":
          return paragraphFontSize({ fontSize: 3.125, marginLeft: 0.3, top: 1.8, left: 0.05, width: 0.6, height: 0.6 });
      }
    })(size)}
`;

const StyledEcoaParagraphIcon = styled.span`
  display: inline-block;
  margin-right: 5px;
  font-size: 3rem;
  vertical-align: middle;
  line-height: 1;
`;
